import {FC, ReactElement} from 'react'
import {InputText, InputTextProps} from '@components/commons/input-text'
import {Dialog, DialogClose, DialogContent, DialogTrigger} from '@components/ui/dialog'
import {Popover, PopoverClose, PopoverContent, PopoverTrigger} from '@components/ui/popover'
import {cn} from '@utilities/helpers'

const hours = [
    '12:00 AM',
    '12:30 AM',
    '1:00 AM',
    '1:30 AM',
    '2:00 AM',
    '2:30 AM',
    '3:00 AM',
    '3:30 AM',
    '4:00 AM',
    '4:30 AM',
    '5:00 AM',
    '5:30 AM',
    '6:00 AM',
    '6:30 AM',
    '7:00 AM',
    '7:30 AM',
    '8:00 AM',
    '8:30 AM',
    '9:00 AM',
    '9:30 AM',
    '10:00 AM',
    '10:30 AM',
    '11:00 AM',
    '11:30 AM',
    '12:00 PM',
    '12:30 PM',
    '1:00 PM',
    '1:30 PM',
    '2:00 PM',
    '2:30 PM',
    '3:00 PM',
    '3:30 PM',
    '4:00 PM',
    '4:30 PM',
    '5:00 PM',
    '5:30 PM',
    '6:00 PM',
    '6:30 PM',
    '7:00 PM',
    '7:30 PM',
    '8:00 PM',
    '8:30 PM',
    '9:00 PM',
    '9:30 PM',
    '10:00 PM',
    '10:30 PM',
    '11:00 PM',
    '11:30 PM'
]

const timeListItemClassName = (isSelected: boolean) =>
    cn(
        'flex p-2.5 w-full cursor-pointer items-center justify-center text-sm',
        isSelected
            ? 'bg-primary-600 font-medium text-white hover:bg-primary-700'
            : 'text-neutral-700 hover:bg-neutral-50 hover:font-medium'
    )

type TimePickerProps = {
    asModal?: boolean
    formatter?: (time: string) => string
    onBlur?: () => void
    disabled?: boolean
    errorMessage?: string
    selectedTime: string
    isTimeDisabled?: (time: string) => boolean
    triggerProps?: Omit<InputTextProps, 'type' | 'readonly' | 'value'>
    onSelectTime: (time: string) => void
}

export const TimePicker: FC<TimePickerProps> = ({
    asModal,
    selectedTime,
    triggerProps,
    formatter,
    onBlur,
    disabled,
    errorMessage,
    isTimeDisabled,
    onSelectTime
}) => {
    const trigger = (
        <InputText
            disabled={disabled}
            {...triggerProps}
            type="text"
            value={(selectedTime && formatter ? formatter(selectedTime) : selectedTime) ?? ''}
            readOnly
            errorMessage={errorMessage}
        />
    )

    const handleOpenChange = (open: boolean) => {
        if (!open) {
            onBlur?.()
        }
    }

    const timeItem = (hour: string): ReactElement => (
        <div className={timeListItemClassName(selectedTime == hour)}>{hour}</div>
    )

    const disabledTimeItem = (hour: string): ReactElement => (
        <div
            key={hour}
            aria-disabled
            className={cn(
                timeListItemClassName(false),
                'cursor-not-allowed text-neutral-300 hover:bg-transparent hover:font-normal'
            )}
        >
            {hour}
        </div>
    )

    if (asModal) {
        return (
            <Dialog onOpenChange={handleOpenChange}>
                <DialogTrigger disabled={disabled} nativeButton={false} render={trigger} />
                <DialogContent
                    className="flex h-97.5 w-80 flex-col overflow-y-auto rounded-sm"
                    showCloseButton={false}
                    forceRenderOverlay={true}
                >
                    {hours.map(hour =>
                        isTimeDisabled?.(hour) ? (
                            disabledTimeItem(hour)
                        ) : (
                            <DialogClose
                                key={hour}
                                nativeButton={false}
                                onClick={() => onSelectTime(hour)}
                                render={timeItem(hour)}
                            />
                        )
                    )}
                </DialogContent>
            </Dialog>
        )
    }

    return (
        <Popover onOpenChange={handleOpenChange}>
            <PopoverTrigger disabled={disabled} render={trigger} />
            <PopoverContent className="h-97.5 w-80 rounded-sm border-0">
                {hours.map(hour =>
                    isTimeDisabled?.(hour) ? (
                        disabledTimeItem(hour)
                    ) : (
                        <PopoverClose
                            key={hour}
                            nativeButton={false}
                            onClick={() => onSelectTime(hour)}
                            render={timeItem(hour)}
                        />
                    )
                )}
            </PopoverContent>
        </Popover>
    )
}
